/**
 * Text Harmony セグメント（Colors セクション内、2026-07-10 追加）。
 *
 * トリオ生成時の Text 色相モード（`AppState.textMode`）を切り替える
 * 3 分割セグメント。見た目は arare-panel の作法に従い、uppercase muted
 * header（`.section-label` を小見出しとして再利用）+ 選択 = 黒塗りの
 * セグメントボタン（既存 `.segmented` を 3 列で使う）。
 *
 * 切り替え時は**即座に**現在の Surface / Inner をアンカーにして Text だけ
 * 再生成し、プレビューへ反映する（Surface / Inner は変えない — モードは
 * Text の導出方針のみを変えるため）。Text がロック中の場合はモード値だけ
 * 更新し、色は上書きしない（鍵が縛るのはプログラム上書き全般）。
 */

import { randomHarmoniousTrio, type TextHarmonyMode } from '../../core/color';
import type { AppState, Store } from '../../state';

/** セグメントの並び順とラベル。 */
const MODES: readonly { value: TextHarmonyMode; label: string }[] = [
  { value: 'triad', label: 'Triad' },
  { value: 'echo', label: 'Echo' },
  { value: 'ink', label: 'Ink' },
];

/**
 * Text Harmony セグメントをマウントする。
 *
 * @param root - Colors セクションのコンテナ
 * @param store - グローバルストア
 * @returns teardown 関数
 */
export function mountTextHarmony(root: HTMLElement, store: Store<AppState>): () => void {
  const wrap = document.createElement('div');
  wrap.className = 'text-harmony';

  // uppercase muted header（arare-panel）。セクション内の小見出しとして
  // .section-label をそのまま再利用する。
  const heading = document.createElement('div');
  heading.className = 'section-label';
  heading.textContent = 'Text Harmony';
  wrap.appendChild(heading);

  const seg = document.createElement('div');
  seg.className = 'segmented segmented-3';

  const buttons = new Map<TextHarmonyMode, HTMLButtonElement>();

  const syncActive = (): void => {
    const current = store.get().textMode;
    for (const [value, button] of buttons) {
      button.classList.toggle('active', value === current);
    }
  };

  const apply = (mode: TextHarmonyMode): void => {
    const state = store.get();
    if (mode === state.textMode) return;

    const patch: Partial<AppState> = { textMode: mode };
    // 即時プレビュー: Surface / Inner を今の色のままアンカーに立てて
    // Text だけ新モードで再導出する。Text ロック中は色を上書きしない。
    if (!state.colorLocks.textColor) {
      const trio = randomHarmoniousTrio(
        { surface: state.surfaceColor, inner: state.innerColor },
        mode,
      );
      patch.textColor = trio.text;
    }
    store.set(patch);
  };

  for (const { value, label } of MODES) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.setAttribute('aria-label', `Text harmony: ${label}`);
    button.addEventListener('click', () => apply(value));
    buttons.set(value, button);
    seg.appendChild(button);
  }
  syncActive();

  wrap.appendChild(seg);
  root.appendChild(wrap);

  const unsubscribe = store.subscribe((state, prev) => {
    if (state.textMode !== prev.textMode) syncActive();
  });

  return (): void => {
    unsubscribe();
    wrap.remove();
  };
}
